import Phaser from 'phaser';
import { NEON_PALETTE } from '@/utils/ColorUtils';

interface LaserSource {
  worldX: number;
  worldY: number;
}

/**
 * Renders animated laser beams from laser equipment positions.
 * Uses a shared Graphics object with additive blend for a neon glow effect.
 * Beams sweep via sine wave, synced to beat progress.
 */
export class LaserRenderer {
  private graphics: Phaser.GameObjects.Graphics;
  private sources: LaserSource[] = [];
  private sweepAngle = 0;

  constructor(scene: Phaser.Scene) {
    this.graphics = scene.add.graphics();
    this.graphics.setBlendMode(Phaser.BlendModes.ADD);
    this.graphics.setDepth(500);
  }

  setSources(sources: LaserSource[]): void {
    this.sources = sources;
  }

  /** Called on beat - snap the sweep phase */
  onBeat(beatCount: number): void {
    // Lasers change direction each beat
    this.sweepAngle = beatCount * 0.3;
  }

  /** Update laser rendering. beatProgress is 0-1 within current beat. */
  update(beatProgress: number): void {
    this.graphics.clear();

    if (this.sources.length === 0) return;

    const progress = beatProgress;

    for (let i = 0; i < this.sources.length; i++) {
      const src = this.sources[i];
      const color = NEON_PALETTE[i % NEON_PALETTE.length];
      const alpha = 0.4 + 0.4 * Math.sin(progress * Math.PI);

      // 3 beams per laser, fanning out
      for (let beam = -1; beam <= 1; beam++) {
        const angle = this.sweepAngle + beam * 0.4 + Math.sin(progress * Math.PI * 2 + i) * 0.5;
        const length = 150 + 50 * Math.sin(progress * Math.PI);
        const endX = src.worldX + Math.cos(angle) * length;
        const endY = src.worldY - Math.abs(Math.sin(angle) * length * 0.5) - 20;

        this.graphics.lineStyle(2, color, alpha);
        this.graphics.beginPath();
        this.graphics.moveTo(src.worldX, src.worldY - 16);
        this.graphics.lineTo(endX, endY);
        this.graphics.strokePath();

        // Thinner bright core
        this.graphics.lineStyle(1, 0xffffff, alpha * 0.7);
        this.graphics.beginPath();
        this.graphics.moveTo(src.worldX, src.worldY - 16);
        this.graphics.lineTo(endX, endY);
        this.graphics.strokePath();
      }
    }
  }

  destroy(): void {
    this.graphics.destroy();
  }
}
